import React, { useState, useEffect } from "react";
import Dashboard, { getOverviewTS, IOverview } from "../../../services/api/Dashboard";
import Year, { IYear } from "../../../services/api/Year";
import Month, { IMonth } from "../../../services/api/Month";
import ChannelType, { IChannelType } from "../../../services/api/ChannelType";
import Formatter from "../../../services/utils/Formatter";
import Select from "../../../components/Form/Select";
import Waterfall from "./charts/Waterfall";
import Loading from "../../../components/Loading";

const Overview: React.FC = () => {
	const [loading, isLoading] = useState<Boolean>(true);
	const [years, setYears] = useState<IYear[]>([]);
	const [months, setMonths] = useState<IMonth[]>([]);
	const [channelTypes, setChannelTypes] = useState<IChannelType[]>([]);
	const [current, setOverview] = useState<IOverview | null>(null);
	const [currentYTD, setOverviewYTD] = useState<IOverview | null>(null);

	const [year, setYear] = useState<number>(0);
	const [month, setMonth] = useState<number>(0);
	const [channelType, setChannelType] = useState<string>("segment");
	const [actual] = useState<string>("ACT");
	const [tstype1, setType1] = useState<string>("BP");
	const [tstype2, setType2] = useState<string>("BP");
	const [tstype3] = useState<string>("BP");
	const [tstype2YTD, setType2YTD] = useState<string>("BP");
	const [tstype3YTD] = useState<string>("BP");
	const [tstype6] = useState<string>("BP");
	let actualValue = 0;
	let actualLastYear = 0;
	let actualReference = 0;

	const date = new Date();
	// Se o mês for janeiro (0), ajusta para dezembro do ano anterior
	if (date.getMonth() === 0) {
		date.setFullYear(date.getFullYear() - 1);  // Ajusta o ano para o anterior
		date.setMonth(11);  // Define o mês como dezembro (11)
	} else {
		date.setMonth(date.getMonth() - 1);  // Subtrai 1 do mês, caso não seja janeiro
	}

	const selectReference = [
		{ value: "BP", label: "BP" },
		{ value: "FRE", label: "FRE" },
		{ value: "SRE", label: "SRE" },
	];

	async function LoadOverview(year: number, month: number, channelType: string) {
		Promise.all([
			Dashboard.GetOverview("LATAM", year, month, channelType),
			Dashboard.GetYTDOverview("LATAM", year, month, channelType)
		])
			.then((value) => {
				setOverview(value[0]);
				setOverviewYTD(value[1]);
			})
			.catch(() => undefined);

		actualValue = 0;
		actualLastYear = 0;
		actualReference = 0;
	}

	useEffect(() => {
		isLoading(true);
		Promise.all([Year.GetAll(), Month.GetAll(), ChannelType.GetAll()])
			.then((value) => {
				const years = value[0]?.filter((value) => { return value.active });
				setYears(years);
				setMonths(value[1]);
				setChannelTypes(value[2])

				setYear(date.getFullYear());
				setMonth(date.getMonth() + 1);
				setChannelType(channelTypes[0]?.slug ?? "segment");
			})
			.catch(() => undefined);
	}, []);

	useEffect(() => {
		isLoading(true);
		if (year > 0 && month > 0)
			LoadOverview(year, month, channelType);
	}, [year, month, channelType]);

	useEffect(() => {
		if (current != null && currentYTD != null)
			isLoading(false);

	}, [current, currentYTD]);

	function computeChartData(obj: IOverview, tsValue: number, isYTD: boolean = false) {
		//#region GRAPHS
		//Graph 01
		const valueBaseG: number = getOverviewTS(obj, tstype2);
		const gv: number = tsValue - valueBaseG;
		
		const targetArray = isYTD ? graph1YTDData : graph1Data;
		
		let insertIndex = targetArray.length - 1;
		
		for (let i = 1; i < targetArray.length; i++) {
			if (gv >= targetArray[i].value) {
				insertIndex = i;
				break;
			}
		}
		
		targetArray.splice(insertIndex, 0, {
			category: obj.name,
			value: gv
		});
		
		return targetArray;
		//#endregion
	}

	function mapList(obj: any): any {
		return { value: obj.id, label: obj.name };
	}

	function mapPlanMajor(parentObj: string, obj: IOverview, objYTD: IOverview): any {
		const valueBase: number = getOverviewTS(obj, tstype1);
		const valueBaseYTD: number = getOverviewTS(objYTD, tstype1);

		const tsValue: number = getActual(obj);
		const tsValueYTD: number = (!obj.isApproved && !obj.children) ? 0 : getActualYTD(objYTD, obj);

		let tsValueLY: number = 0;
		let tsValueLYYTD: number = 0;

		if (obj.lastYear != null) {
			tsValueLY = getActual(obj.lastYear);
		}

		if (objYTD.lastYear != null) {
			tsValueLYYTD = getActualYTD(objYTD.lastYear, obj);
		}

		if (obj.children == null) {
			actualValue += tsValueYTD;
			actualLastYear += tsValueLYYTD;
			actualReference += valueBaseYTD;
		}

	function syncArrayOrder(
		sourceArray: Array<{ category: string; value: number }>,
		targetArray: Array<{ category: string; value: number }>
		) {
		const priorityCategories = ["BP", "SRE", "FRE"];

		// Cria o mapa de ordenação do source
		const orderMap = new Map<string, number>();
		sourceArray.forEach((item, index) => {
			orderMap.set(item.category, index);
		});

		// Separar itens por prioridade
		const priority: typeof targetArray = [];
		const known: typeof targetArray = [];
		const unknown: typeof targetArray = [];

		targetArray.forEach(item => {
			if (priorityCategories.includes(item.category)) {
			priority.push(item);
			} else if (orderMap.has(item.category)) {
			known.push(item);
			} else {
			unknown.push(item);
			}
		});

		// Ordenar os prioritários de acordo com a ordem fixa BP > SRE > FRE
		priority.sort((a, b) => (
			priorityCategories.indexOf(a.category) - priorityCategories.indexOf(b.category)
		));

		// Ordenar os conhecidos conforme a ordem do sourceArray
		known.sort((a, b) => (
			orderMap.get(a.category)! - orderMap.get(b.category)!
		));

		// Ordenar os desconhecidos alfabeticamente
		unknown.sort((a, b) => a.category.localeCompare(b.category));

		return [...priority, ...known, ...unknown];
		}



		graph1YTDData = syncArrayOrder(graph1Data, graph1YTDData);


		if (obj.level == 1 && objYTD.level == 1)
			computeChartData(obj, tsValue) && computeChartData(objYTD, tsValueYTD, true);
			
		var rowClass = obj.code != "LATAM" && parentObj != "" ? "childrenRow childrenRow-" + parentObj : "";
		var childrenCollapse = obj.children != null ? (
			<React.Fragment key={'child' + obj.id}>
				{obj.children.map((childObj: IOverview, i: number) => mapPlanMajor(parentObj + "-" + obj.id, childObj, objYTD.children[i]))}
			</React.Fragment>
		) : null;

		var childrenCollapseButton = obj.children != null ? (
			<span><a className="childrenButton" data-row-class={"childrenRow-" + parentObj + "-" + obj.id} onClick={toggleChildren} title="Toggle">{obj.name}</a></span>
		) : <span>{obj.name}</span>;

		function toggleChildren(e: any) {
			e.target.classList.toggle("childrenButton--open");
			var isOpen = e.target.classList.contains("childrenButton--open");

			//hide or show row
			document.querySelectorAll("[class^='childrenRow " + e.target.dataset.rowClass + "']").forEach(element => {

				//close all descendant buttons
				element.querySelectorAll(".childrenButton").forEach(bt => { bt.classList.remove("childrenButton--open") });

				if (isOpen) {
					//show only imediate children
					if (element.classList.contains(e.target.dataset.rowClass)) {
						element.classList.add("childrenRow--hidden");
						element.classList.remove("childrenRow--open")
					}
				}
				else {
					//close all descendants
					element.classList.remove("childrenRow--hidden");
					element.classList.add("childrenRow--open");
				}

			});
		}

		//display value if actual is not approved
		return (
			<React.Fragment key={obj.id}>
				<tr className={rowClass}>
					<td className="table__data--small">
						{childrenCollapseButton}
					</td>

					<td>{Formatter.DisplayValue(tsValue)}</td>
					<td>{Formatter.DisplayPercent((tsValue / tsValueLY - 1) * 100)}</td>
					<td className="table__data--border">{Formatter.DisplayPercent((tsValue / valueBase - 1) * 100)}</td>

					{/* TRADE YTD */}
					<td>{Formatter.DisplayValue(tsValueYTD)}</td>
					<td>{Formatter.DisplayPercent((tsValueYTD / tsValueLYYTD - 1) * 100)}</td>
					<td className="table__data--border">{Formatter.DisplayPercent((tsValueYTD / valueBaseYTD - 1) * 100)}</td>

					<td className="table__separator"></td>
					<td className="table__data--small">{obj.approver}</td>
				</tr>
				{childrenCollapse}
			</React.Fragment>
		);
	}

	function mapPlanAll(obj: IOverview, i: number): any {
		const valueBase: number = getOverviewTS(obj, tstype1);
		const tsValue: number = getActual(obj);

		let tsValueLY: number = 0;

		if (obj.lastYear != null) {
			tsValueLY = getActual(obj.lastYear);
		}

		return (
			<tr key={obj.id + tsValue} className="table__data--totals">
				<td>{obj.name}</td>
				
				{/* MONTH */}
				<td>{Formatter.DisplayValue(tsValue)}</td>
				<td>{Formatter.DisplayPercent((tsValue / tsValueLY - 1) * 100)}</td>
				<td className="table__data--border">{Formatter.DisplayPercent((tsValue / valueBase - 1) * 100)}</td>

				{/* YTD */}
				<td>{Formatter.DisplayValue(actualValue)}</td>
				<td>{Formatter.DisplayPercent((actualValue / actualLastYear - 1) * 100)}</td>
				<td className="table__data--border">{Formatter.DisplayPercent((actualValue/ actualReference - 1) * 100)}</td>

				<td className="table__separator"></td>

				<td className="table__data--small">{obj.approver}</td>
			</tr>
		);
	}

	const getMonthName = (month: number) => months.find(i => i.id === month)?.name ?? ''

	function getOverviewData(obj: IOverview): void {
		if (obj.featured)
			overviewDataMajor.push(obj);

		if (obj.children !== null)
			obj.children.forEach((objChild: IOverview) => {
				getOverviewData(objChild);
			});
		else if (!obj.featured)
			overviewData.push(obj);
	}

	function getOverviewYTDData(obj: IOverview): void {
		if (obj.featured)
			overviewYTDDataMajor.push(obj);

		if (obj.children !== null)
			obj.children.forEach((objChild: IOverview) => {
				getOverviewYTDData(objChild);
			});
		else if (!obj.featured)
			overviewYTDData.push(obj);
	}

	function getActualYTD(objYTD: IOverview, obj: IOverview): number {
		let valueBase: number = 0;

		//consider: (YTD Actual value) - (current month/week Actual)
		if (obj)
			valueBase = objYTD.actual - obj.actual;


		return valueBase + getActual(obj);
	}

	function getActual(obj: IOverview): number {
		let valueBase: number = 0;

		valueBase += obj.actual;

		return valueBase;
	}

	let g1Total = 0;
	let g1FC: number = 0;
	let g1YTDTotal = 0;
	let g1YTDFC: number = 0;
	let graph1Data: any = [];
	let graph2Data: any = [];
	let graph1YTDData: any = [];
	let graph2YTDData: any = [];
	const overviewDataMajor: any = [];
	const overviewYTDDataMajor: any = [];
	let overviewData: any = [];
	let overviewYTDData: any = [];

	let overviewAllData: any = [];

	if (!loading) {
		getOverviewData(current!);
		getOverviewYTDData(currentYTD!);

		// Major Graph1
		g1Total = 0;
		g1FC = 0;

		overviewDataMajor.forEach((obj: IOverview, index: number) => {
			g1Total += getOverviewTS(obj, tstype2);
			g1FC += getActual(obj);
		});


		graph1Data = [
			{
				category: tstype2,
				value: g1Total
			},
			{
				category: "Actual",
				value: g1FC
			}
		];

		// Major Graph1 ytd
		g1YTDTotal = 0;
		g1YTDFC = 0;

		overviewYTDDataMajor.forEach((obj: IOverview, index: number) => {
			g1YTDTotal += getOverviewTS(obj, tstype2);
			g1YTDFC += getActual(obj);
		});

		graph1YTDData = [
			{
				category: tstype2YTD,
				value: g1YTDTotal
			},
			{
				category: "Actual",
				value: g1YTDFC
			}
		];

		let allOthers: IOverview = {
			name: 'All Others',
			planBusinessPlan: 0,
			planFRE: 0,
			actual: 0,
			planSRE: 0,
			year: 0,
		} as IOverview;
		let allOthersAll: IOverview = {
			name: 'All Others',
			planBusinessPlan: 0,
			planFRE: 0,
			planSRE: 0,
			actual: 0,
			year: 0,
			lastYear: {
				name: 'All Others',
				planBusinessPlan: 0,
				planFRE: 0,
				planSRE: 0,
				actual: 0,
				year: 0,
			} as IOverview
		} as IOverview;

		overviewData.forEach((obj: IOverview, index: number) => {

			if (obj.level === 1) {
				allOthersAll.planBusinessPlan += obj.planBusinessPlan;
				allOthersAll.planFRE += obj.planFRE;
				allOthersAll.planSRE += obj.planSRE;
				allOthersAll.actual += obj.actual;

				allOthersAll.year = obj.year;

				allOthersAll.lastYear.planBusinessPlan += obj.lastYear.planBusinessPlan;
				allOthersAll.lastYear.planFRE += obj.lastYear.planFRE;
				allOthersAll.lastYear.planSRE += obj.lastYear.planSRE;
				allOthersAll.lastYear.actual += obj.lastYear.actual;

				allOthersAll.lastYear.year = obj.lastYear.year;
			}

			if (index > 8) {
				allOthers.planBusinessPlan += obj.planBusinessPlan;
				allOthers.planFRE += obj.planFRE;
				allOthers.planSRE += obj.planSRE;
				allOthers.actual += obj.actual;

				allOthers.year = obj.year;
			}
		});
		overviewData.splice(9);
		overviewData = overviewData.concat(allOthers);

		// Others Graph2
		g1Total = 0;
		g1FC = 0;
		overviewData.forEach((obj: IOverview, index: number) => {
			const tsValue = getOverviewTS(obj, tstype6);
			const valueBaseG = getActual(obj);

			g1Total += tsValue;
			g1FC += valueBaseG

			const gv: number = valueBaseG - tsValue;

			if (graph2Data.length === 0)
				graph2Data.push({
					category: obj.name,
					value: gv
				});
			else {
				for (let ii = 0; ii <= graph2Data.length; ii++) {
					if (ii === graph2Data.length || gv >= graph2Data[ii].value) {
						graph2Data.splice(ii, 0, {
							category: obj.name,
							value: gv
						});
						break;
					}
				}
			}
		});

		let allOthersYTD: IOverview = {
			name: 'All Others',
			planBusinessPlan: 0,
			planFRE: 0,
			actual: 0,
			planSRE: 0,
			year: 0,
		} as IOverview;
		let allOthersAllYTD: IOverview = {
			name: 'All Others',
			planBusinessPlan: 0,
			planFRE: 0,
			planSRE: 0,
			actual: 0,
			year: 0,
			lastYear: {
				name: 'All Others',
				planBusinessPlan: 0,
				planFRE: 0,
				planSRE: 0,
				actual: 0,
				year: 0,
			} as IOverview
		} as IOverview;

		overviewYTDData.forEach((obj: IOverview, index: number) => {
			if (obj.level === 1) {
				allOthersAllYTD.planBusinessPlan += obj.planBusinessPlan;
				allOthersAllYTD.planFRE += obj.planFRE;
				allOthersAllYTD.planSRE += obj.planSRE;
				allOthersAllYTD.actual += obj.actual;

				allOthersAllYTD.year = obj.year;

				allOthersAllYTD.lastYear.planBusinessPlan += obj.lastYear.planBusinessPlan;
				allOthersAllYTD.lastYear.planFRE += obj.lastYear.planFRE;
				allOthersAllYTD.lastYear.planSRE += obj.lastYear.planSRE;
				allOthersAllYTD.lastYear.actual += obj.lastYear.actual;

				allOthersAllYTD.lastYear.year = obj.lastYear.year;
			}

			if (index > 8) {
				allOthersYTD.planBusinessPlan += obj.planBusinessPlan;
				allOthersYTD.planFRE += obj.planFRE;
				allOthersYTD.planSRE += obj.planSRE;
				allOthersYTD.actual += obj.actual;

				allOthersYTD.year = obj.year;
			}
		});
		overviewYTDData.splice(9);
		overviewYTDData = overviewYTDData.concat(allOthersYTD);

		graph2Data.splice(0, 0, {
			category: tstype3,
			value: g1Total
		});
		graph2Data.push({
			category: "Actual",
			value: g1FC
		});

		g1YTDTotal = 0;
		g1YTDFC = 0;
		overviewYTDData.forEach((obj: IOverview, index: number) => {
			const tsValue = getOverviewTS(obj, tstype3YTD);
			const valueBaseG = getActual(obj);

			g1YTDTotal += tsValue;
			g1YTDFC += valueBaseG

			const gv: number = valueBaseG - tsValue;

			if (graph2YTDData.length === 0)
				graph2YTDData.push({
					category: obj.name,
					value: gv
				});
			else {
				for (let ii = 0; ii <= graph2Data.length; ii++) {
					if (ii === graph2YTDData.length || gv >= graph2YTDData[ii].value) {
						graph2YTDData.splice(ii, 0, {
							category: obj.name,
							value: gv
						});
						break;
					}
				}
			}

		});

		graph2YTDData.splice(0, 0, {
			category: tstype3YTD,
			value: g1YTDTotal
		});

		graph2YTDData.push({
			category: "Actual",
			value: g1YTDFC
		});

		overviewAllData = [current];

		allOthers = {
			name: 'All Others',
			planBusinessPlan: 0,
			planFRE: 0,
			planSRE: 0,
			actual: 0,
			year: 0,
		} as IOverview;
		allOthersAll = {
			name: 'All Others',
			planBusinessPlan: 0,
			planFRE: 0,
			planSRE: 0,
			actual: 0,
			year: 0,
		} as IOverview;

		overviewYTDData.forEach((obj: IOverview, index: number) => {
			allOthersAll.planBusinessPlan += obj.planBusinessPlan;
			allOthersAll.planFRE += obj.planFRE;
			allOthersAll.planSRE += obj.planSRE;
			allOthersAll.actual += obj.actual;

			allOthersAll.year = obj.year;

			if (index > 8) {
				allOthers.planBusinessPlan += obj.planBusinessPlan;
				allOthers.planFRE += obj.planFRE;
				allOthers.planSRE += obj.planSRE;
				allOthers.actual += obj.actual;

				allOthers.year = obj.year;
			}
		});


		overviewYTDData.splice(9);
		overviewYTDData = overviewYTDData.concat(allOthers);

	}

	//this needs to be proccessed before latamTotal
	var allChildren = overviewDataMajor.map((obj: IOverview, i: number) => mapPlanMajor("", obj, overviewYTDDataMajor[i]));
	var latamTotal = overviewAllData.map(mapPlanAll);

	return (
		<React.Fragment>
			{loading ? (
				<Loading />
			) : (
				<React.Fragment>
					<section className="content__block">
						{/* <p>Table 1</p> */}
						<div className="content__header">
							<h2 className="content__title">Atlas - {current!.code}</h2>
							<div className="multi-selects">
								<div className="content__select">
									{/* <Select
										className="select-custom select-custom--medium select-custom--shadow"
										name="channelType"
										title="Type"
										options={channelTypes.map(mapChannelTypeList)}
										value={channelType}
										handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
											var value = event.currentTarget.value;
											if (value) {
												setChannelType(value);
											}

										}}
									/> */}

									<Select
										className="select-custom select-custom--medium select-custom--shadow"
										name="month"
										title="Month"
										options={months.map(mapList)}
										value={month}
										handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
											var value = +event.currentTarget.value;
											if (value) {
												setMonth(value);
											}

										}}
									/>
								</div>
								<div className="content__select">
									<Select
										className="select-custom select-custom--medium select-custom--shadow"
										name="year"
										title="Year"
										options={years.map((obj) => { return { value: obj.name, label: obj.name } })}
										value={year}
										handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
											var value = +event.currentTarget.value;
											if (value)
												setYear(value);
										}}
									/>
								</div>
							</div>
						</div>

						<div className="content__select">
							<Select
								name="tstype1"
								title="Reference"
								options={selectReference}
								value={tstype1}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									setType1(event.currentTarget.value);
									setType2(event.currentTarget.value);
									setType2YTD(event.currentTarget.value);
								}}
							/>
						</div>

						<div className="table table--last-th-stylized">
							<table className="table__data has-table__data--totals table--row-align-text-right">
								<tbody>
									<tr className="table__data--borderless">
										<td></td>
										<td colSpan={4}>
											<span className="table_subtitle">
												ACTUAL {current!.year}
											</span>
										</td>
										<td colSpan={4}>
											<span className="table_subtitle">YTD</span>
										</td>
										<td></td>
									</tr>
									<tr>
										<th>BUSINESS <br />UNIT</th>
										<th>{actual}</th>
										<th>VS. LY</th>
										<th>VS. {tstype1}</th>

										<th>{actual}</th>
										<th>VS. LY</th>
										<th>VS. {tstype1}</th>

										<th>SIGNED <br />OFF BY</th>
									</tr>
									{latamTotal}
									{allChildren}
								</tbody>
							</table>
						</div>
					</section>

					{/*#region Month*/}

					<section className="content__block">
						{/* <p>GRAPH 1 (graph1Data)</p> */}
						<h2 className="content__title">Month | {getMonthName(month)} - {year} </h2>

						<div className="content__select">
							<Select
								name="tstype2"
								title="Reference"
								options={selectReference}
								value={tstype2}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									setType1(event.currentTarget.value);
									setType2(event.currentTarget.value);
									setType2YTD(event.currentTarget.value);
								}}
							/>
						</div>

						<Waterfall
							id={"chart1div"}
							data={graph1Data}
						/>
					</section>

					{/* <section className="content__block">

						<div className="content__select">
							<Select
								name="tstype3"
								title="Reference"
								options={selectReference}
								value={tstype3}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									setType3(event.currentTarget.value);
								}}
							/>
						</div>
						<br />
						<p>{getMonthName(month)} - {year}</p>
						<Waterfall
							id={"chart2div"}
							data={graph2Data}
						/>
					</section> */}
					{/*#endregion*/}

					{/*#region YTD*/}
					<section className="content__block">
						<h2 className="content__title">YTD | {getMonthName(month)} - {year} </h2>
						<div className="content__select">
							<Select
								name="tstype2ytd"
								title="Reference"
								options={selectReference}
								value={tstype2YTD}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									setType1(event.currentTarget.value);
									setType2(event.currentTarget.value);
									setType2YTD(event.currentTarget.value);
								}}
							/>
						</div>

						<Waterfall
							id={"chart1ytddiv"}
							data={graph1YTDData}
						/>
					</section>

					{/* <section className="content__block">

						<div className="content__select">
							<Select
								name="tstype3ytd"
								title="Reference"
								options={selectReference}
								value={tstype3YTD}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									setType3YTD(event.currentTarget.value);
								}}
							/>
						</div>
						<br />
						<p>{getMonthName(month)} - {year}</p>
						<Waterfall
							id={"chart2ytddiv"}
							data={graph2YTDData}
						/>
					</section> */}
					{/*#endregion*/}

					{/* <section className="content__block">
						<div className="content__select">
							<Select
								name="tstype4"
								title="Reference"
								options={selectReference}
								value={tstype4}
								handleChange={(event: React.FormEvent<HTMLSelectElement>) => {
									setType4(event.currentTarget.value);
								}}
							/>
						</div>

						<div className="table">
							<table className="table__data has-table__data--totals table--row-align-text-right">
								<tbody>
									<tr className="table__data--borderless">
										<td colSpan={15}>
											<span className="table_subtitle">Plan COMPARISON | {getMonthName(month)} - {year}</span>
										</td>
									</tr>
									<tr>
										<th>BUSINESS <br />UNIT</th>

										<th>{tstype4}</th>
										<th>{actual}</th>
										<th className="table__separator"></th>
										<th>VS. {tstype4}</th>
										<th>VS ACT</th>
									</tr>
									{overviewAllData.map((obj: IOverview) => mapWeek(obj, true))}
									{overviewDataMajor.map((obj: IOverview) => mapWeek(obj, false))}
								</tbody>
							</table>
						</div>
					</section> */}

				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default Overview;
